import { type ReactEventHandler, useCallback, useMemo, useState } from 'react';
import type { Product } from 'reactApp/types/Billing';
import type { SwitchOption } from 'reactApp/ui/TariffCardNew/components/TariffSwitch.types';
import type { ITariffCardProps } from 'reactApp/ui/TariffCardNew/TariffCard.types';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import type { TariffsByTabProductsType } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';
import { isMonth3Period, isMonth6Period, isMonthPeriod } from 'reactApp/utils/Period';

import { useMonthSaving } from './useMonthSaving';
import { useYearSaving } from './useYearSaving';

interface IUseSwitchProductsReturn extends Pick<ITariffCardProps, 'onClickSwitch' | 'switchChecked' | 'switchOptions'> {
    showSwitch: boolean;
    product: Product;
    yearSavingDiscount?: number;
    monthSavingDiscount?: number;
    isYearFallbackProduct?: boolean;
}

type TSwitchProducts = (props: {
    activeTab?: ETabsName;
    products: TariffsByTabProductsType;
    withSwitch?: boolean;
}) => IUseSwitchProductsReturn;

export const useSwitchProducts: TSwitchProducts = ({ activeTab, products, withSwitch = true }) => {
    const [switchChecked, setMonthSwitchChecked] = useState(false);
    const showSwitch = withSwitch && !!products.monthProduct;

    // если свитч не показываем, то основной продукт годовой
    const isMonthProduct =
        (showSwitch && switchChecked && products.monthProduct && (activeTab === ETabsName.pro || ETabsName.usual)) ||
        (activeTab === ETabsName.month && products.monthProduct);

    let product = products.yearProduct;

    // для случаев, когда в свитче выбран месячный продукт, но доступен только годовой
    const isYearFallbackProduct = activeTab === ETabsName.month && !products.monthProduct;

    if (isMonthProduct && products.monthProduct) {
        product = products.monthProduct;
    }

    const [yearSaving, yearSavingDiscount] = useYearSaving(products);
    const [_, monthSavingDiscount] = useMonthSaving(products.monthProduct);

    const onClickSwitch: ReactEventHandler = useCallback((event) => {
        event.stopPropagation();
        setMonthSwitchChecked((checked) => !checked);
    }, []);

    const switchOptions: [SwitchOption, SwitchOption] = useMemo(() => {
        const mapSwitchTextToPeriodFn = {
            ['На месяц']: isMonthPeriod,
            ['На 3 месяца']: isMonth3Period,
            ['На 6 месяцев']: isMonth6Period,
        };

        return [
            {
                value: products.yearProduct.period,
                text: 'На год',
                note: yearSaving ? `Экономия ${yearSaving} ₽` : '',
            },
            {
                value: products.monthProduct?.period || '',
                text: showSwitch
                    ? Object.keys(mapSwitchTextToPeriodFn).find((text) => mapSwitchTextToPeriodFn[text](products.monthProduct?.period))
                    : '',
            },
        ];
    }, [products, yearSaving, showSwitch]);

    return {
        product,
        showSwitch,
        onClickSwitch,
        switchChecked,
        switchOptions,
        yearSavingDiscount,
        monthSavingDiscount,
        isYearFallbackProduct,
    };
};
